import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView, useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Skeleton, Tag, Toast, Typography } from '@chilecompra/react-kit/components'
import { inIframe } from '@chilecompra/react-kit/utils'
import {
  REACT_APP_MERCADO_PUBLICO_BASE_URL,
  REACT_APP_FEATURE_FLAG_MESSAGE_INFO
} from '../../config/settings/environment'
import { ABILITY_STATUS_TYPES, USER_TYPE } from '../../config/settings/constant'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import {
  onSetWaitingListRecalculateProcessThunk,
  onGetAbilityStatusDetailThunk,
  onGetValidateButtonRecalculateThunk
} from './AbilityStatusSection.actions'
import IconWithTooltip from './AbilityStatusSection.tooltip'
import ButtonWithPopover from './AbilityStatusSection.buttonPopover'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import RecalculateAbilitySection from './AbilityStatusSection.recalculate'
import AbilityStatusSectionModal from './AbilityStatusSection.modal'
import {
  AbilityReason,
  AbilityReasonContent,
  AbilityStatusWrapper,
  Container,
  Content,
  ErrorIcon,
  ExclamationIcon,
  HeadingWrapper,
  Link,
  Opacity,
  Separator,
  Strong,
  SuccessIcon,
  WarningIcon
} from './AbilityStatusSection.styles'
import PrivateSection from '../../components/Security/PrivateSection'

/**
 * The ability status section container.
 */
const AbilityStatusSection = () => {
  const dispatch = useDispatch()
  const { userType } = useAuthorization()
  const dni = usePathParam('rut')
  const isForeign = checkIfForeignDni(dni)
  const [showDialog, setShowDialog] = useState(false)
  const [disability, setDisability] = useState('')

  const {
    size: { isDesktop, isMobile, isSmall }
  } = useViewport()

  const { abilityStatus, orgLegalName } = useSelector(state => state.basicSellerInformation)
  const { abilityDetails, error, loading, recalculateAbilityButton } = useSelector(state => state.abilityStatus)

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])
  const [showDialogConfirmation, setShowDialogConfirmation] = useState(false)

  const numberPerDayRecalculate = useMemo(() => {
    const returnValue = recalculateAbilityButton.recalculateHours ? 24 / recalculateAbilityButton.recalculateHours : 8
    return returnValue
  }, [recalculateAbilityButton])

  const handleDialogClose = () => {
    if (!loading) {
      setShowDialogConfirmation(false)
    }
  }

  const handleDialogOpen = () => {
    if (!loading) {
      setShowDialogConfirmation(true)
    }
  }

  const handleConfirmRecalculateDialog = () => {
    dispatch(onSetWaitingListRecalculateProcessThunk({ dni }))
    handleDialogClose()
  }

  useEffectOnce(() => {
    if (!loading) return
    if (userType === USER_TYPE.PUBLIC) {
      dispatch(onGetAbilityStatusDetailThunk({ dni, userType }))
    } else {
      dispatch(onGetAbilityStatusDetailThunk({ dni, userType }))
      !isForeign && dispatch(onGetValidateButtonRecalculateThunk({ dni }))
    }
  }, [isForeign])

  useAnalyticsPageView('/ficha/habilidad')

  const abilityDetailsText = useCallback(() => {
    switch (abilityStatus) {
      case 'HABIL':
        return (
          <Typography variant="body2">
            Proveedor <Strong>{orgLegalName}</Strong> se encuentra en estado{' '}
            <Strong>{ABILITY_STATUS_TYPES[abilityStatus]?.label}</Strong>, por no afectarle ninguna de las causales de
            inhabilidad descritas en el artículo 154 del Reglamento de la Ley N°19.886 de Compras Públicas.
          </Typography>
        )
      case 'INHABIL':
        return (
          <Typography variant="body2">
            Proveedor <Strong>{orgLegalName}</Strong> se encuentra en estado{' '}
            <Strong>{ABILITY_STATUS_TYPES[abilityStatus]?.label}</Strong>, por afectarle alguna de las siguientes
            causales de inhabilidad descritas en el artículo 154 del Reglamento de la Ley N°19.886 de Compras Públicas.
          </Typography>
        )
      case 'SIN HABILIDAD':
        return (
          <Typography variant="body2">
            Esta empresa proveedora no ha realizado el pago su acreditación en el Registro de Proveedores, por lo que no
            podemos disponibilizar su estado de habilidad.
          </Typography>
        )
      default:
        return (
          <Typography variant="body2">
            Estamos en proceso de revisión de la información del proveedor <Strong>{orgLegalName}</Strong>. Por el
            momento, su estado de habilidad se encuentra en estado{' '}
            <Strong>{ABILITY_STATUS_TYPES[abilityStatus]?.label}</Strong>.
          </Typography>
        )
    }
  }, [abilityStatus])

  const abilityDetailsTextForeign = useCallback(() => {
    switch (abilityStatus) {
      case 'HABIL':
        return (
          <Typography variant="body2">
            El proveedor <Strong>{orgLegalName}</Strong> se encuentra inscrito y{' '}
            <Strong>{ABILITY_STATUS_TYPES[abilityStatus]?.label}</Strong>, en el Registro de Proveedores, pues se
            constató que no se encuentra en la lista de personas naturales y jurídicas declaradas inelegibles para la
            adjudicación de contratos elaborada por el Banco Interamericano de Desarrollo.
          </Typography>
        )
      default:
        return (
          <Typography variant="body2">
            El proveedor <Strong>{orgLegalName}</Strong>. no se encuentra inscrito en el Registro de Proveedores, por
            tanto, no se cuenta con información sobre su estado de habilidad.
          </Typography>
        )
    }
  }, [abilityStatus])

  const toggleDialog = (value = '') => {
    setDisability(value)
    setShowDialog(!showDialog)
  }

  const handleViewMore = key => () => toggleDialog(key)

  const handleOpenModalCallback = () => {
    if (isInIframe) {
      window.parent.postMessage(
        {
          action: 'scrollTo',
          value: {
            x: 0,
            y: 0
          }
        },
        REACT_APP_MERCADO_PUBLICO_BASE_URL
      )
    }
  }

  return error?.reason === 'FORBIDDEN_ERROR' ? null : (
    <Content>
      <HeadingWrapper alignItems={loading ? 'initial' : 'baseline'}>
        <Typography fontWeight="bold" padding="0 0 32px" variant="h4">
          Estado de habilidad
        </Typography>

        {!error && (
          <Tag
            color={ABILITY_STATUS_TYPES[abilityStatus]?.color}
            enableIcon={ABILITY_STATUS_TYPES[abilityStatus]?.withIcon || false}
            loading={loading}
            margin={loading ? '-7px 0 0 22px' : '0 0 0 22px'}
            text={ABILITY_STATUS_TYPES[abilityStatus]?.label}
          />
        )}
      </HeadingWrapper>

      {!loading && error && (
        <Toast
          border
          severity="error"
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor"
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}

      {(!error || error.reason === 'SOMETHING_WENT_WRONG_ERROR_RECALCULATE') && (
        <AbilityStatusWrapper display={loading ? 'block' : 'flex'}>
          {!loading && abilityStatus === 'SIN INFORMACIÓN' && <ExclamationIcon padding="2px 10px 0 22px" />}

          {loading ? (
            <>
              <Skeleton width="100%" />
              <Skeleton width="75%" />
            </>
          ) : isForeign ? (
            abilityDetailsTextForeign()
          ) : (
            abilityDetailsText()
          )}
        </AbilityStatusWrapper>
      )}

      {!isForeign && (
        <PrivateSection>
          <ProtectByFeatures rule={features => features['com.seller/recalculate.ability.button']}>
            <RecalculateAbilitySection
              loading={loading}
              showDialog={showDialogConfirmation}
              numberPerDay={numberPerDayRecalculate}
              handleDialogClose={handleDialogClose}
              handleDialogOpen={handleDialogOpen}
              handleConfirmRecalculateDialog={handleConfirmRecalculateDialog}
              recalculateAbilityButton={recalculateAbilityButton}
            />
          </ProtectByFeatures>
        </PrivateSection>
      )}

      {!error && abilityStatus !== 'SIN INFORMACIÓN' && (
        <Typography fontWeight="bold" padding="48px 0 4px" variant="h4">
          {loading ? <Skeleton width="40%" /> : 'Detalle habilidad'}
        </Typography>
      )}

      {loading && (
        <>
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
          <Skeleton height={70} margin="8px 0 0" variant="rect" width="100%" />
        </>
      )}

      {(!error || error.reason === 'SOMETHING_WENT_WRONG_ERROR_RECALCULATE') &&
        !loading &&
        abilityStatus !== 'SIN INFORMACIÓN' &&
        abilityDetails
          .filter(reason => (isForeign ? reason.key === 'LISTAS NEGRAS' : true))
          .map((reason, index) => {
            return (
              <AbilityReason
                key={`key-${index}`}
                error={reason.status ?? true}
                display={isMobile ? 'block' : 'flex'}
                reasonKey={REACT_APP_FEATURE_FLAG_MESSAGE_INFO === 'true' ? reason.key : ''}
              >
                {isMobile && reason.status !== null && !reason.status && <SuccessIcon padding="0 100% 10px 0" />}
                {isMobile && reason.status !== null && reason.status && <ErrorIcon padding="6px 2px 1px 0" />}
                {isMobile && reason.status === null && <ButtonWithPopover padding="0 0 5px" />}

                <AbilityReasonContent>
                  <Typography fontWeight="bold" variant="body2">
                    {reason.status === null ? <Opacity>{reason.title}</Opacity> : reason.title}
                  </Typography>
                  <Typography variant="subtitle">
                    {reason.status === null ? <Opacity>{reason.subtitle}</Opacity> : reason.subtitle}
                  </Typography>
                </AbilityReasonContent>

                {reason.key === 'DEUDAS LABORALES' && REACT_APP_FEATURE_FLAG_MESSAGE_INFO === 'true'
                  ? !isMobile && reason.status !== null && reason.status && <WarningIcon padding="2px 10px 30px 22px" />
                  : !isMobile &&
                    reason.status !== null &&
                    reason.status && <SuccessIcon padding="2px 10px 30px 22px" />}

                {!isMobile && reason.status !== null && !reason.status && <ErrorIcon padding="0 22px 6px 10px" />}
                {isDesktop && !isSmall && reason.status === null && <IconWithTooltip />}
                {(!isDesktop || isSmall) && !isMobile && reason.status === null && (
                  <ButtonWithPopover padding="2px 10px 0 22px" />
                )}

                {reason.key === 'DEUDAS LABORALES' ? (
                  <Container>
                    <Typography
                      display="block"
                      margin="0"
                      variant={REACT_APP_FEATURE_FLAG_MESSAGE_INFO === 'true' ? 'subtitle' : 'body2'}
                      fontSize="14px"
                    >
                      {reason.status === null ? <Opacity>{reason.description}</Opacity> : reason.description}
                    </Typography>{' '}
                    <br />
                    {REACT_APP_FEATURE_FLAG_MESSAGE_INFO === 'true' && (
                      <Typography display="block" margin="0" variant="body2">
                        <strong>Importante:</strong> La información no se encuentra disponible debido a un problema
                        originado en la fuente oficial.
                      </Typography>
                    )}
                    {reason.status !== null && !reason.status && (
                      <Link onClick={handleViewMore(reason.key)}>Ver más</Link>
                    )}
                  </Container>
                ) : (
                  <Container>
                    <Typography display="block" margin="0" variant="body2">
                      {reason.status === null ? <Opacity>{reason.description}</Opacity> : reason.description}
                    </Typography>
                    {reason.status !== null && !reason.status && (
                      <Link onClick={handleViewMore(reason.key)}>Ver más</Link>
                    )}
                  </Container>
                )}
              </AbilityReason>
            )
          })}

      {!error && !loading && abilityStatus === 'SIN INFORMACIÓN' && <Separator />}
      {!error && showDialog ? (
        <AbilityStatusSectionModal
          isOpen={showDialog}
          onClose={toggleDialog}
          onOpenModal={handleOpenModalCallback}
          disability={disability}
        />
      ) : null}
    </Content>
  )
}

export default AbilityStatusSection
